import { BaseOrganizationFormData } from '../../organizations/types/organizationTypes';

export interface FarmOrganizationData extends BaseOrganizationFormData {
  // Farm-specific fields - Updated to match database schema
  _key?: string;
  owner_id?: string;
  registration_number?: string;
  farm_type?: string[] | string; // Can be array or single value
  production_focus?: string; // Changed from string[] to string
  country_code?: string;
  is_favorite?: boolean;
  location?: {
    address: string;
    region: string;
    postal_code: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  area_hectares?: number;
  capacity_head?: number;
  facilities?: string[]; // Changed from object to array
  certifications?: string[];
  contact_info?: {
    phone: string;
    email: string;
    emergency_contact: string;
  };
  is_active?: boolean;
  
  // Collaborators field
  collaborators?: Array<{
    user_id: string;
    user_name: string;
    role: string;
    manager_assignments?: Array<{
      manager_type: string;
      permissions: string[];
    }>;
  }>;
}

export interface Farm {
  _key?: string;
  _id?: string;
  _rev?: string;
  name: string;
  registration_number?: string;
  farm_type: 'dairy' | 'beef' | 'mixed' | 'other';
  location: {
    address: string;
    region: string;
    postal_code: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  contact_info?: {
    phone: string;
    email: string;
    emergency_contact: string;
  };
  operational_details?: {
    area_hectares?: number;
    capacity_head?: number;
    facilities?: {
      milking_parlor: boolean;
      feed_storage: boolean;
      veterinary_facilities: boolean;
      quarantine_area: boolean;
    };
  };
  certifications?: string[];
  created_at?: string;
  updated_at?: string;
  is_active?: boolean;
}

export interface FarmSearchFilters {
  _key?: string;
  name?: string;
  farm_type?: 'dairy' | 'beef' | 'mixed' | 'other';
  registration_number?: string;
  is_active?: boolean;
  created_at?: {
    $gte?: string;
    $lte?: string;
  };
}

export interface CreateFarmRequest {
  name: string;
  registration_number?: string;
  farm_type: 'dairy' | 'beef' | 'mixed' | 'other';
  location: {
    address: string;
    region: string;
    postal_code: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  contact_info?: {
    phone: string;
    email: string;
    emergency_contact: string;
  };
  operational_details?: {
    area_hectares?: number;
    capacity_head?: number;
    facilities?: {
      milking_parlor: boolean;
      feed_storage: boolean;
      veterinary_facilities: boolean;
      quarantine_area: boolean;
    };
  };
  certifications?: string[];
}

export interface UpdateFarmRequest {
  name?: string;
  registration_number?: string;
  farm_type?: 'dairy' | 'beef' | 'mixed' | 'other';
  location?: Partial<{
    address: string;
    region: string;
    postal_code: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  }>;
  contact_info?: Partial<{
    phone: string;
    email: string;
    emergency_contact: string;
  }>;
  operational_details?: Partial<{
    area_hectares?: number;
    capacity_head?: number;
    facilities?: {
      milking_parlor: boolean;
      feed_storage: boolean;
      veterinary_facilities: boolean;
      quarantine_area: boolean;
    };
  }>;
  certifications?: string[];
  is_active?: boolean;
}

export interface FarmFormFields {
  name: {
    label: string;
    placeholder: string;
    type: 'text';
    required: boolean;
  };
  area_hectares: {
    label: string;
    placeholder: string;
    type: 'number';
    suffix: string;
    required: boolean;
  };
  farm_type: {
    label: string;
    options: Array<{ value: string; label: string }>;
    required: boolean;
  };
  capacity_head: {
    label: string;
    placeholder: string;
    type: 'number';
    required: boolean;
  };
  registration_number: {
    label: string;
    placeholder: string;
    type: 'text';
    required: boolean;
  };
  production_focus: {
    label: string;
    options: Array<{ value: string; label: string }>;
    required: boolean;
  };
  country_code: {
    label: string;
    placeholder: string;
    type: 'text';
    required: boolean;
  };
  location: {
    address: {
      label: string;
      placeholder: string;
      type: 'text';
      required: boolean;
    };
    region: {
      label: string;
      placeholder: string;
      type: 'text';
      required: boolean;
    };
    postal_code: {
      label: string;
      placeholder: string;
      type: 'text';
      required: boolean;
    };
  };
  contact_info: {
    phone: {
      label: string;
      placeholder: string;
      type: 'text';
      required: boolean;
    };
    email: {
      label: string;
      placeholder: string;
      type: 'email';
      required: boolean;
    };
    emergency_contact: {
      label: string;
      placeholder: string;
      type: 'text';
      required: boolean;
    };
  };
}

export const farmFormConfig: FarmFormFields = {
  name: {
    label: 'Nome da Fazenda',
    placeholder: 'Ex: Fazenda São João',
    type: 'text',
    required: true,
  },
  area_hectares: {
    label: 'Área Total',
    placeholder: 'Ex: 150',
    type: 'number',
    suffix: 'hectares',
    required: true,
  },
  farm_type: {
    label: 'Tipo de Fazenda',
    options: [
      { value: 'beef', label: 'Gado de Corte' },
      { value: 'dairy', label: 'Gado Leiteiro' },
      { value: 'mixed', label: 'Misto (Corte + Leite)' },
      { value: 'other', label: 'Outros Animais' },
    ],
    required: true,
  },
  capacity_head: {
    label: 'Capacidade (Cabeças)',
    placeholder: 'Ex: 500',
    type: 'number',
    required: true,
  },
  registration_number: {
    label: 'Número do CAR',
    placeholder: 'Ex: CAR-1234567',
    type: 'text',
    required: false,
  },
  production_focus: {
    label: 'Foco de Produção',
    options: [
      { value: 'milk_production', label: 'Produção de Leite' },
      { value: 'meat_production', label: 'Produção de Carne' },
      { value: 'breeding', label: 'Reprodução' },
      { value: 'mixed_production', label: 'Produção Mista' },
    ],
    required: true,
  },
  country_code: {
    label: 'Código do País',
    placeholder: 'Ex: BR',
    type: 'text',
    required: true,
  },
  location: {
    address: {
      label: 'Endereço',
      placeholder: 'Ex: Rua da Fazenda, 123',
      type: 'text',
      required: true,
    },
    region: {
      label: 'Região/Estado',
      placeholder: 'Ex: São Paulo',
      type: 'text',
      required: true,
    },
    postal_code: {
      label: 'CEP',
      placeholder: 'Ex: 12345-678',
      type: 'text',
      required: true,
    },
  },
  contact_info: {
    phone: {
      label: 'Telefone',
      placeholder: 'Ex: +55 11 99999-9999',
      type: 'text',
      required: true,
    },
    email: {
      label: 'Email',
      placeholder: 'Ex: fazenda@exemplo.com',
      type: 'email',
      required: true,
    },
    emergency_contact: {
      label: 'Contato de Emergência',
      placeholder: 'Ex: +55 11 88888-8888',
      type: 'text',
      required: true,
    },
  },
};

export const farmCertificationOptions = [
  'Orgânico',
  'Rastreabilidade SISBOV',
  'Bem-estar Animal',
  'Certificação Ambiental',
  'ISO 14001',
  'Rainforest Alliance',
  'Global GAP',
];

// ERP Types for Farm Financial Management
export interface BusinessContact {
  id: string;
  contact_name: string;
  contact_type: 'customer' | 'supplier' | 'service_provider' | 'government' | 'other';
  business_category: 'feed_supplier' | 'veterinary' | 'equipment' | 'transport' | 'dairy_processor' | 'meat_processor' | 'financial' | 'other';
  registration_number?: string;
  tax_id?: string;
  contact_info: {
    primary_contact?: string;
    email?: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
  payment_terms?: string;
  credit_limit?: number;
  preferred_currency?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface InventoryItem {
  id: string;
  farm_id: string;
  item_name: string;
  item_category: 'feed' | 'medication' | 'fertilizer' | 'seed' | 'fuel' | 'equipment' | 'supplies' | 'other';
  sku?: string;
  description?: string;
  unit_of_measure: 'kg' | 'liters' | 'doses' | 'bags' | 'units' | 'tons' | 'gallons';
  quantity_on_hand: number;
  reorder_level?: number;
  max_stock_level?: number;
  unit_cost?: number;
  total_value?: number;
  supplier_id?: string;
  storage_location?: string;
  expiry_date?: string;
  batch_lot_number?: string;
  last_updated: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinancialTransaction {
  id: string;
  farm_id: string;
  transaction_date: string;
  amount: number;
  currency: string;
  type: 'income' | 'expense';
  category: 'cattle_sale' | 'milk_sale' | 'feed_purchase' | 'veterinary' | 'equipment' | 'labor' | 'utilities' | 'insurance' | 'taxes' | 'loan_payment' | 'other';
  subcategory?: string;
  description?: string;
  business_contact_id?: string;
  invoice_id?: string;
  cattle_id?: string;
  payment_method: 'cash' | 'check' | 'bank_transfer' | 'credit_card' | 'other';
  reference_number?: string;
  tax_amount?: number;
  is_tax_deductible: boolean;
  fiscal_year?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  invoice_type: 'receivable' | 'payable';
  context: 'farm_operations' | 'platform_billing';
  business_contact_id?: string;
  issue_date: string;
  due_date: string;
  line_items: {
    description: string;
    quantity: number;
    unit_price: number;
    total_price: number;
  }[];
  subtotal: number;
  tax_amount?: number;
  total_amount: number;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  payment_terms?: string;
  paid_date?: string;
  paid_amount?: number;
  payment_reference?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ERP Summary types for dashboard views
export interface InventorySummary {
  total_items: number;
  low_stock_items: number;
  expired_items: number;
  total_value: number;
  categories: {
    category: string;
    count: number;
    value: number;
  }[];
}

export interface FinancialSummary {
  total_income: number;
  total_expenses: number;
  net_profit: number;
  pending_receivables: number;
  pending_payables: number;
  monthly_trend: {
    month: string;
    income: number;
    expenses: number;
  }[];
}

export interface InvoiceSummary {
  total_invoices: number;
  paid_invoices: number;
  overdue_invoices: number;
  total_amount_due: number;
  recent_invoices: Invoice[];
}
